import React, { FC, useCallback, useEffect } from "react";

import Page from "../common/navigation/Page";

import FormFields from "../common/form/FieldsList";
import FormButtons from "../common/form/Buttons";

import F7 from "../../utils/F7";
import { useNavigate, useParams } from "react-router-dom";
import { useCurrentInventory } from "../../hooks/database/useCurrentInventory";
import { useUpdateInventory } from "../../hooks/database/usePouchUpdate";
import { useCurrentInventoryId } from "../../hooks/url";
import { useForm } from "../../hooks/useForm";
import BackButton from "../common/navigation/BackButton";

const colors = {
  White: "rgba(252, 245, 226, 0.6)",
  Yellow: "rgba(193, 168, 14, 0.6)",
  Orange: "rgba(204, 132, 0, 0.6)",
  Red: "rgba(204, 0, 0, 0.6)",
  Magenta: "rgba(210, 54, 130, 0.6)",
  Violet: "rgba(108, 113, 196, 0.6)",
  Blue: "rgba(0, 0, 142, 0.6)",
  Cyan: "rgba(42, 161, 152, 0.6)",
  Green: "rgba(80, 156, 7, 0.6)",
  Black: "rgba(0, 0, 0, 0.6)",
  Gray: "rgba(119, 119, 119, 0.6)",
};

const InventoryStyleEditPage: FC = () => {
  const { inventoryId } = useCurrentInventoryId();
  const { data: inventory } = useCurrentInventory();
  const { execute: updateInventory } = useUpdateInventory();
  const { style: styleName } = useParams<{ style: string }>();

  const getDropdownFields = useCallback(
    (form: string) => {
      const forms = inventory?.forms;
      if (!forms || !forms[form]) {
        return [];
      }

      return Object.values(forms[form].fields)
        .filter((field) => field.type === "dropdown")
        .map((field) => field.label + " | " + field.key);
    },
    [inventory?.forms],
  );

  const getFormFieldKeyFromValue = useCallback((value: string) => {
    const parts = value.split(" | ");

    if (parts.length === 2) {
      return parts[1];
    } else {
      return value;
    }
  }, []);

  const getFormFieldValueAsOptions = useCallback(
    (form: string, field: string): string[] => {
      const formField = getFormFieldKeyFromValue(field);
      const forms = inventory?.forms;
      if (forms && forms[form]) {
        const field = forms[form].fields[formField];
        if ("options" in field && field.options) {
          return Array.isArray(field.options) ? [...field.options] : field.options.split("\n");
        }
      }
      return [];
    },
    [getFormFieldKeyFromValue, inventory?.forms],
  );

  const { setValues, values, fields, handleChange } = useForm({
    form: {
      label: "Form",
      type: "dropdown",
      required: true,
      options: Object.keys(inventory?.forms || {}),
    },
    formField: {
      label: "Form Field",
      type: "dropdown",
      required: true,
      options: (values) => (values.form ? getDropdownFields(values.form as string) : []),
    },
    formFieldValue: {
      label: "Form Field Value",
      type: "dropdown",
      required: true,
      options: (values) =>
        values.form && values.formField ? getFormFieldValueAsOptions(values.form as string, values.formField as string) : [],
    },
    color: {
      label: "Color",
      type: "dropdown",
      required: true,
      options: Object.keys(colors),
    },
    borderColor: {
      label: "Border Color",
      type: "dropdown",
      required: true,
      options: Object.keys(colors),
    },
  });

  useEffect(() => {
    if (styleName && inventory && inventory.styles && styleName in inventory.styles) {
      const style = inventory.styles[styleName];
      const form = inventory.forms[style.form];
      const field = form.fields[style.formField];
      setValues({
        form: style.form,
        formField: field.label + " | " + field.key,
        formFieldValue: style.formFieldValue,
        color: Object.entries(colors).find(([_, value]) => value === style.color)![0],
        borderColor: Object.entries(colors).find(([_, value]) => value === style.borderColor)![0],
      });
    } else {
      setValues({
        form: null,
        formField: null,
        formFieldValue: null,
        color: null,
        borderColor: null,
      });
    }
  }, [inventory, setValues, styleName]);

  const getFormFieldLabelFromValue = useCallback((value: string) => {
    const parts = value.split(" | ");
    if (parts.length === 2) {
      return parts[0];
    } else {
      return value;
    }
  }, []);

  const navigate = useNavigate();
  const handleSave = useCallback(async () => {
    if (!inventory || !inventoryId) {
      return;
    }

    if ((values.form as string).length === 0) {
      F7.alert("", "'Form' can't be empty!");
      return;
    }
    if ((values.formField as string).length === 0) {
      F7.alert("", "'Form Field' can't be empty!");
      return;
    }
    // @ts-ignore
    const formFieldValue = getFormFieldKeyFromValue(values.formField);
    const styleKey = values.form + "_" + formFieldValue + "_" + values.formFieldValue;
    // @ts-ignore
    const styleName = getFormFieldLabelFromValue(values.formField) + " in " + values.form;
    const style = {
      key: styleKey,
      name: styleName,
      form: values.form as string,
      formField: formFieldValue,
      formFieldValue: values.formFieldValue as string,
      // @ts-ignore
      color: colors[values.color],
      // @ts-ignore
      borderColor: colors[values.borderColor],
    };

    await updateInventory(inventoryId, {
      ...inventory,
      styles: {
        ...inventory.styles,
        [styleKey]: style,
      },
    });

    navigate(-1);
  }, [
    inventory,
    inventoryId,
    getFormFieldKeyFromValue,
    values.formField,
    values.form,
    values.formFieldValue,
    values.color,
    values.borderColor,
    getFormFieldLabelFromValue,
    updateInventory,
    navigate,
  ]);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <Page title="Style Editor" navbarLeft={<BackButton onClick={handleCancel} />} noToolbar>
      <div>
        <FormFields fields={fields} onChange={handleChange} />
      </div>
      <FormButtons saveText="Save" onSave={handleSave} />
    </Page>
  );
};

export default InventoryStyleEditPage;
